"use client"

import { useState, useCallback, useRef, useEffect } from 'react'
import { useSearchParams } from 'next/navigation'
import Sentry from '@/lib/sentry-safe'
import { ChevronDown, AlertTriangle } from 'lucide-react'
import dynamic from 'next/dynamic'
import { FileDropzone } from '@/components/create/file-dropzone'

// The waveform trimmer only matters after a file is
// dropped — keep it out of the route's initial JS.
const WaveformTrimmer = dynamic(
  () => import('@/components/create/waveform-trimmer').then(m => m.WaveformTrimmer),
  {
    ssr: false,
    loading: () => (
      <div
        aria-hidden="true"
        className="h-32 rounded-[var(--ls-radius-0)] border border-[color:var(--tls-border-subtle)] bg-[var(--tls-surface-elevated)]"
      />
    ),
  }
)
import { AudioControls } from '@/components/create/audio-controls'
import { LoudnessControls } from '@/components/create/loudness-controls'
import { FadeControls } from '@/components/create/fade-controls'
import { CompatibilityIndicator } from '@/components/create/compatibility-indicator'
import { DownloadPanel } from '@/components/create/download-panel'
import {
  decodeAudioFile,
  getAudioMetadataFromBuffer,
  measureLoudnessDb,
  optimizeForTeslaWithFades,
  TESLA_REQUIREMENTS,
  LOUDNESS_PRESETS,
  AudioHeaderError,
  type AudioMetadata,
  type LoudnessPreset,
} from '@/lib/audio-utils'
import { readDeclaredSampleRate } from '@/lib/audio-header'
import { soundsApi } from '@/lib/api/sounds'
import { storageApi } from '@/lib/api/storage'
import { downloadAccountTarget, libraryExportSoundId } from '@/lib/download-accounting'
import { downloadFailureMessage } from '@/lib/state-copy'
import { isContentBlocked } from '@/lib/sound-content-block'
import { trackCreateStep, trackDownloadCompleted, trackDownloadButtonClick, trackDownloadInitiated, trackPreviewPlay } from '@/lib/tracking'
import { useDownload } from '@/hooks/useDownload'
import { recordPendingInstall } from '@/components/ui/install-checkin'
import { markDownloadCompleted } from '@/lib/download-attribution'
import { cn } from '@/lib/utils'
import { useSessionLog } from '@/components/create/session-log'
import { ActivityRailDesktop, ActivityStatusRow } from '@/components/create/activity-rail'
import { UndoHistory } from '@/components/create/undo-history'
import { SectionHead } from '@/components/ui/tls/section-head'
import { ReturnStateBanner } from '@/components/create/return-state-banner'
import { FileMismatchModal } from '@/components/create/file-mismatch-modal'
import { TESLA_FACTS } from '@/lib/tesla-facts'
import {
  saveReturnState,
  loadReturnState,
  clearReturnState,
  matchesReturnState,
  type CreateReturnState,
} from '@/components/create/create-session-persistence'

type PreviewMode = 'original' | 'processed'

// Below this measured RMS, the clip reads as effectively silent to a listener.
const SILENCE_THRESHOLD_DB = -50

// §19.3 WARN state: below this the source is clearly degraded (an 8 kHz
// phone-call-quality recording, say) — still usable, worth naming so the
// user isn't surprised by a muffled result. Tesla's own target is 44.1 kHz.
const LOW_SAMPLE_RATE_THRESHOLD_HZ = 22050

// Enough to clear an ID3 tag and reach the first frame or fmt chunk.
const HEADER_READ_BYTES = 1024 * 1024

function formatFadeMs(value: number): string {
  if (value === 0) return 'off'
  return `${(value * 1000).toFixed(0)}ms`
}

const teslaRequirementItems = [
  {
    label: 'Format',
    copy: `WAV, ${TESLA_FACTS.audio.recommended.sampleRate}, ${TESLA_FACTS.audio.recommended.bitDepth}, stereo`,
  },
  {
    label: 'Length',
    copy: `Up to ${TESLA_FACTS.audio.recommended.maxDurationSeconds} seconds`,
  },
  {
    label: 'File size',
    copy: TESLA_FACTS.audio.recommended.maxFileSize.replace(/^u/, 'U'),
  },
  {
    label: 'File name',
    // --tls-brand is 3.21:1 on dark canvas — a graphic-only floor (3:1), a
    // fail for 4.5:1 text. --tls-brand-ink (5.20:1) is the text-safe
    // variant; see app/globals.css and lib/guide-ui.ts's inlineTouchLinkClass.
    copy: (
      <code className="break-words font-mono text-[var(--tls-brand-ink)]">
        LockChime.wav
      </code>
    ),
  },
] as const

// eslint-disable-next-line react-doctor/no-giant-component -- create page orchestrates file upload, waveform trimming, audio controls, and download; decomposing further would require a global store
// eslint-disable-next-line react-doctor/prefer-useReducer -- multiple independent state slices across async boundaries; useReducer would not simplify this pattern
export default function CreatePageClient() {
  const searchParams = useSearchParams()

  // File state
  const [selectedFile, setSelectedFile] = useState<File | null>(null)
  const [audioBuffer, setAudioBuffer] = useState<AudioBuffer | null>(null)
  const [metadata, setMetadata] = useState<AudioMetadata | null>(null)
  const [isLoadingFile, setIsLoadingFile] = useState(false)
  const [fileError, setFileError] = useState<string | null>(null)
  // §19.3 WARN state: low sample rate is usable, not a failure — a distinct,
  // dismissable notice rather than a blocking error.
  const [lowSampleRateWarning, setLowSampleRateWarning] = useState<string | null>(null)
  // Label shown when loading via ?sound=<id> deep-link
  const [deepLinkLabel, setDeepLinkLabel] = useState<string | null>(null)
  // Deep-link sound id (from ?sound=<id>) — drives the full PostDownloadPanel
  // in DownloadPanel instead of the generic no-soundId fallback.
  const deepLinkSoundId = searchParams.get('sound') || undefined

  // Trim state
  const [regionStart, setRegionStart] = useState<number>(0)
  const [regionEnd, setRegionEnd] = useState<number>(TESLA_REQUIREMENTS.MAX_DURATION)

  // Loudness options (replace volumePercent)
  const [loudnessPreset, setLoudnessPreset] = useState<LoudnessPreset>('normal')
  const [fineTuneGainDb, setFineTuneGainDb] = useState(0)
  const [fadeIn, setFadeIn] = useState(0.05)
  const [fadeOut, setFadeOut] = useState(0.1)

  // Playback state
  const [isPlaying, setIsPlaying] = useState(false)
  const [isLoopEnabled, setIsLoopEnabled] = useState(true)
  const [previewMode, setPreviewMode] = useState<PreviewMode>('original')

  // Processing state — stores processed output alongside the settings used to produce it.
  // processedBlob/processedAudioBuffer below are null when any setting has changed since processing.
  const [isProcessing, setIsProcessing] = useState(false)
  const [downloadedBlob, setDownloadedBlob] = useState<Blob | null>(null)
  const [processedAudio, setProcessedAudio] = useState<{
    blob: Blob
    audioBuffer: AudioBuffer
    loudnessPreset: LoudnessPreset
    fineTuneGainDb: number
    fadeIn: number
    fadeOut: number
    regionStart: number
    regionEnd: number
    isNearSilent: boolean
  } | null>(null)
  // Measured loudness of the processed result for the live readout
  const [measuredLoudnessDb, setMeasuredLoudnessDb] = useState<number | null>(null)
  const { download: downloadFile } = useDownload()

  // Progressive disclosure: loudness + fade controls are closed by default
  // (PRODUCT.md — users are not audio engineers; sensible defaults already apply)
  const [isFineTuneOpen, setIsFineTuneOpen] = useState(false)

  // §19.2 / §19.10 — one session log, two views (the rail and the undo stack).
  const sessionLog = useSessionLog()
  const sessionStartedRef = useRef(false)
  useEffect(() => {
    if (sessionStartedRef.current) return
    sessionStartedRef.current = true
    // SILENT per the status-routing table: written to the log, not announced.
    sessionLog.addEntry('session', 'Session started', { undoable: false })
    // eslint-disable-next-line react-hooks/exhaustive-deps -- fire exactly once on mount
  }, [])

  // §19.10.1 — the return state. A waiting record from a previous visit this
  // tab, checked once on mount and cleared as soon as it is resolved (either
  // by a matching re-drop or by the mismatch modal's explicit discard).
  const [pendingReturnState, setPendingReturnState] = useState<CreateReturnState | null>(() => {
    // Lazy initializer, not an effect: this is a one-time read of what a
    // PREVIOUS page-load left in sessionStorage, not a subscription to an
    // external system, so it belongs in render rather than in a setState-
    // in-effect that would cost an extra cascading render for nothing.
    if (searchParams.get('sound')) return null // deep-link flow re-fetches by id instead
    return loadReturnState()
  })
  const [mismatch, setMismatch] = useState<{
    file: File
    buffer: AudioBuffer
    meta: AudioMetadata
  } | null>(null)

  // Refs holding the last-committed value of each editable property, read by
  // the log/undo wiring below so an inverse can be built without threading
  // every setter through extra state.
  const regionRef = useRef({ start: regionStart, end: regionEnd })
  useEffect(() => { regionRef.current = { start: regionStart, end: regionEnd } })
  const fadeRef = useRef({ fadeIn, fadeOut })
  useEffect(() => { fadeRef.current = { fadeIn, fadeOut } })
  const gainRef = useRef(fineTuneGainDb)
  useEffect(() => { gainRef.current = fineTuneGainDb })
  const gainLogTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const gainDragStartRef = useRef<number | null>(null)

  // §19.10 persistence table: values, fades and gain survive a refresh; the
  // decoded audio does not. Deep-linked sounds skip this — a refresh there
  // re-fetches by id through the effect above instead.
  useEffect(() => {
    if (!audioBuffer || !selectedFile || deepLinkLabel) return
    saveReturnState({
      fileName: selectedFile.name,
      fileSize: selectedFile.size,
      fileDuration: metadata?.duration ?? 0,
      regionStart,
      regionEnd,
      fadeIn,
      fadeOut,
      fineTuneGainDb,
      loudnessPreset,
    })
  }, [audioBuffer, selectedFile, deepLinkLabel, metadata, regionStart, regionEnd, fadeIn, fadeOut, fineTuneGainDb, loudnessPreset])

  // §19.10 "navigating away or closing — GUARDED, with the file name in the
  // prompt." Browsers ignore custom copy in the native dialog, but the
  // returnValue is still required to trigger it at all.
  useEffect(() => {
    if (!audioBuffer || !selectedFile) return
    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault()
      e.returnValue = `Leave without exporting ${selectedFile.name}? Your edits are kept, but the audio itself will need to be dropped again.`
      return e.returnValue
    }
    window.addEventListener('beforeunload', handler)
    return () => window.removeEventListener('beforeunload', handler)
  }, [audioBuffer, selectedFile])

  // Derive processed outputs: valid only when all current settings match what was used
  const processedBlob =
    processedAudio?.loudnessPreset === loudnessPreset &&
    processedAudio?.fineTuneGainDb === fineTuneGainDb &&
    processedAudio?.fadeIn === fadeIn &&
    processedAudio?.fadeOut === fadeOut &&
    processedAudio?.regionStart === regionStart &&
    processedAudio?.regionEnd === regionEnd
      ? processedAudio.blob
      : null
  const processedAudioBuffer = processedBlob ? (processedAudio?.audioBuffer ?? null) : null
  // Only surface the silent-clip warning while it reflects the currently valid processed output
  const isNearSilent = processedBlob !== null && processedAudio?.isNearSilent === true

  // After an upload the trim controls sit a screen below the dropzone. Bring
  // them into view and give them focus once, for uploads only (a deep link
  // loading on page open should not take focus).
  const moveToTrimRef = useRef(false)
  const trimHeadingRef = useRef<HTMLHeadingElement | null>(null)
  useEffect(() => {
    if (!audioBuffer || !moveToTrimRef.current) return
    moveToTrimRef.current = false
    const heading = trimHeadingRef.current
    if (!heading) return
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches
    heading.scrollIntoView({ block: 'start', behavior: reduceMotion ? 'auto' : 'smooth' })
    heading.focus({ preventScroll: true })
  }, [audioBuffer])

  // Audio element for playback
  const audioRef = useRef<HTMLAudioElement | null>(null)
  const audioUrlRef = useRef<string | null>(null)

  // Cleanup audio URL on unmount
  useEffect(() => {
    const urlRef = audioUrlRef;
    return () => {
      if (urlRef.current) {
        URL.revokeObjectURL(urlRef.current)
      }
    }
  }, [])

  // Deep-link: ?sound=<id> — fetch sound from library and prefill editor on mount.
  // Uses searchParams (stable across renders) so this effect only fires once.
  useEffect(() => {
    const soundId = searchParams.get('sound')
    if (!soundId) return

    let cancelled = false
    // eslint-disable-next-line react-hooks/set-state-in-effect -- initializing loading state for async deep-link fetch is intentional, equivalent to loading state on mount
    setIsLoadingFile(true)
    // eslint-disable-next-line react-hooks/set-state-in-effect -- clearing errors on mount is intentional
    setFileError(null)
    // eslint-disable-next-line react-hooks/set-state-in-effect -- clearing label on mount is intentional
    setDeepLinkLabel(null)

    ;(async () => {
      try {
        // 1. Fetch sound metadata
        const sound = await soundsApi.getSound(soundId)
        if (cancelled) return
        // TES-1733: a content-blocked row is treated as missing here too —
        // otherwise `/create?sound=<uuid>` deep-links the pulled audio straight
        // into the converter, title and all, bypassing the sound page's 404.
        if (!sound || isContentBlocked(sound)) {
          setFileError('Sound not found. Upload a file to continue.')
          setIsLoadingFile(false)
          return
        }

        setDeepLinkLabel(sound.title)

        // 2. Resolve the audio URL
        const audioUrl = await storageApi.getAudioUrl(sound.file_url)
        if (cancelled) return
        if (!audioUrl) {
          setFileError(`Couldn't load "${sound.title}". Upload a file to continue.`)
          setIsLoadingFile(false)
          return
        }

        // 3. Fetch the raw audio bytes. Supabase URLs resolve directly with CORS
        // support; unexpected external hosts remain behind the proxy via getAudioUrl.
        // audio-egress-allow: trimmer on /create: the ?sound=<id> deep link or the file the visitor exports
        const response = await fetch(audioUrl)
        if (cancelled) return
        if (!response.ok) {
          setFileError(`Couldn't fetch "${sound.title}". Upload a file to continue.`)
          setIsLoadingFile(false)
          return
        }

        // 4. Decode into an AudioBuffer
        const arrayBuffer = await response.arrayBuffer()
        if (cancelled) return
        // Built before decoding so the file carries its real byte size.
        const libraryFile = new File([arrayBuffer], `${sound.title}.wav`, { type: 'audio/wav' })
        const audioContext = new AudioContext()
        // audio-egress-allow: trimmer on /create: the ?sound=<id> deep link or the file the visitor exports
        const buffer = await audioContext.decodeAudioData(arrayBuffer)
        await audioContext.close()
        if (cancelled) return

        const meta = getAudioMetadataFromBuffer(buffer)

        setSelectedFile(libraryFile)
        setAudioBuffer(buffer)
        setMetadata(meta)
        setRegionStart(0)
        setRegionEnd(Math.min(TESLA_REQUIREMENTS.MAX_DURATION, meta.duration))
        setIsLoadingFile(false)
      } catch (error) {
        if (cancelled) return
        Sentry.captureException(error)
        setFileError('That library sound did not load. Upload a file to continue.')
        setIsLoadingFile(false)
      }
    })()

    return () => { cancelled = true }
    // searchParams is stable; intentionally run once on mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const formatFileSizeForLog = (bytes: number): string =>
    bytes < 1024 * 1024 ? `${(bytes / 1024).toFixed(0)} KB` : `${(bytes / 1024 / 1024).toFixed(1)} MB`

  // Applies a return-state record's waiting values to the freshly-decoded
  // buffer that matched it, and records the re-attachment (§19.10.1 rule 3:
  // "the stack re-arms, the rail appends Work re-attached, and nothing else
  // changes").
  const reattachReturnState = useCallback((state: CreateReturnState) => {
    setRegionStart(state.regionStart)
    setRegionEnd(state.regionEnd)
    setFadeIn(state.fadeIn)
    setFadeOut(state.fadeOut)
    setFineTuneGainDb(state.fineTuneGainDb)
    setLoudnessPreset(state.loudnessPreset as LoudnessPreset)
    sessionLog.addEntry('session', 'Work re-attached', { undoable: false })
    setPendingReturnState(null)
    clearReturnState()
  }, [sessionLog])

  // Handle file selection
  const handleFileSelect = useCallback(async (file: File) => {
    setFileError(null)
    setLowSampleRateWarning(null)
    setIsLoadingFile(true)
    setProcessedAudio(null)

    try {
      const buffer = await decodeAudioFile(file)
      const declaredRate = readDeclaredSampleRate(new Uint8Array(await file.slice(0, HEADER_READ_BYTES).arrayBuffer()))
      const meta = {
        ...getAudioMetadataFromBuffer(buffer),
        ...(declaredRate ? { sourceSampleRate: declaredRate } : {}),
      }

      // §19.3 "empty" state: the header decodes but there is no audio data —
      // a distinct cause from "corrupt", and worth naming separately per
      // docs/ANTI_SLOP.md's no-generic-error rule.
      if (!Number.isFinite(meta.duration) || meta.duration <= 0) {
        setFileError('That file contains no audio. It decodes to zero seconds, which usually means the download failed part way.')
        setSelectedFile(null)
        setAudioBuffer(null)
        setMetadata(null)
        return
      }

      // §19.3 WARN state: usable but degraded, named rather than blocked.
      const sourceRate = meta.sourceSampleRate ?? meta.sampleRate
      if (sourceRate < LOW_SAMPLE_RATE_THRESHOLD_HZ) {
        setLowSampleRateWarning(
          `This file is ${(sourceRate / 1000).toFixed(1)} kHz. We can use it, but we will resample to 44.1 kHz. It will still sound muffled through the car's exterior speaker, because the detail is not in the file.`
        )
      }

      setSelectedFile(file)
      setAudioBuffer(buffer)
      setMetadata(meta)
      moveToTrimRef.current = true
      trackCreateStep({ tool: 'convert', step: 'file_selected' })

      sessionLog.addEntry('file', `Loaded ${file.name} · ${formatFileSizeForLog(file.size)}`, { undoable: false })

      if (pendingReturnState) {
        if (matchesReturnState(pendingReturnState, file, meta.duration)) {
          reattachReturnState(pendingReturnState)
          return
        }
        // §19.10.1 rule 4: a different file asks first — never silently
        // apply the old file's trim points to a differently-shaped clip.
        setMismatch({ file, buffer, meta })
        return
      }

      // Set initial region to first 5 seconds or full duration
      setRegionStart(0)
      setRegionEnd(Math.min(TESLA_REQUIREMENTS.MAX_DURATION, meta.duration))
    } catch (error) {
      Sentry.captureException(error)
      if (error instanceof AudioHeaderError) {
        // §19.3 "torn header" state: the file's own bytes never matched a
        // known audio container — distinct from a header that decodes fine
        // but whose payload does not (docs/ANTI_SLOP.md rule 5).
        setFileError(error.message)
        trackCreateStep({ tool: 'convert', step: 'failed', reason: 'torn_header' })
      } else {
        // §19.3 "corrupt" state: the header was readable enough for the
        // browser to attempt a decode, but the audio data was not.
        setFileError(`${file.name} is damaged and cannot be decoded. If it plays elsewhere, export it again and retry.`)
        trackCreateStep({ tool: 'convert', step: 'failed', reason: 'decode' })
      }
      setSelectedFile(null)
      setAudioBuffer(null)
      setMetadata(null)
    } finally {
      setIsLoadingFile(false)
    }
  }, [pendingReturnState, reattachReturnState, sessionLog])

  const handleMismatchReapply = useCallback(() => {
    if (!mismatch || !pendingReturnState) return
    setRegionStart(Math.min(pendingReturnState.regionStart, mismatch.meta.duration))
    setRegionEnd(Math.min(pendingReturnState.regionEnd, mismatch.meta.duration))
    setFadeIn(pendingReturnState.fadeIn)
    setFadeOut(pendingReturnState.fadeOut)
    setFineTuneGainDb(pendingReturnState.fineTuneGainDb)
    setLoudnessPreset(pendingReturnState.loudnessPreset as LoudnessPreset)
    sessionLog.addEntry('session', `Re-applied waiting edits to ${mismatch.file.name}`, { undoable: false })
    setPendingReturnState(null)
    clearReturnState()
    setMismatch(null)
  }, [mismatch, pendingReturnState, sessionLog])

  const handleMismatchDiscard = useCallback(() => {
    if (!mismatch) return
    setRegionStart(0)
    setRegionEnd(Math.min(TESLA_REQUIREMENTS.MAX_DURATION, mismatch.meta.duration))
    sessionLog.addEntry('session', 'Discarded waiting edits (different file)', { undoable: false })
    setPendingReturnState(null)
    clearReturnState()
    setMismatch(null)
  }, [mismatch, sessionLog])

  // Handle file clear
  const handleFileClear = useCallback(() => {
    setSelectedFile(null)
    setAudioBuffer(null)
    setMetadata(null)
    setFileError(null)
    setLowSampleRateWarning(null)
    setProcessedAudio(null)
    setDownloadedBlob(null)
    setMeasuredLoudnessDb(null)
    setDeepLinkLabel(null)
    setRegionStart(0)
    setRegionEnd(TESLA_REQUIREMENTS.MAX_DURATION)
    setIsFineTuneOpen(false)

    if (audioUrlRef.current) {
      URL.revokeObjectURL(audioUrlRef.current)
      audioUrlRef.current = null
    }
  }, [])

  // Handle region change from trimmer — processed audio invalidates automatically via derivation.
  // §19.4/§19.10: every commit (the end of a drag, not each move of it)
  // gets a rail entry with an inverse, so trims are undoable like everything else.
  const handleRegionChange = useCallback((start: number, end: number) => {
    const before = regionRef.current
    setRegionStart(start)
    setRegionEnd(end)
    if (before.start === start && before.end === end) return
    sessionLog.addEntry('trim', `Trimmed to ${start.toFixed(2)} → ${end.toFixed(2)} s`, {
      undoable: true,
      undo: () => { setRegionStart(before.start); setRegionEnd(before.end) },
      redo: () => { setRegionStart(start); setRegionEnd(end) },
    })
  }, [sessionLog])

  // Fade presets are discrete clicks (fade-controls.tsx), so each one is its
  // own undoable log entry rather than needing to be debounced.
  const handleFadeInChange = useCallback((value: number) => {
    const before = fadeRef.current.fadeIn
    setFadeIn(value)
    if (before === value) return
    sessionLog.addEntry('fade', `Fade in ${value === 0 ? 'off' : `${(value * 1000).toFixed(0)}ms`}`, {
      undoable: true,
      undo: () => setFadeIn(before),
      redo: () => setFadeIn(value),
    })
  }, [sessionLog])
  const handleFadeOutChange = useCallback((value: number) => {
    const before = fadeRef.current.fadeOut
    setFadeOut(value)
    if (before === value) return
    sessionLog.addEntry('fade', `Fade out ${value === 0 ? 'off' : `${(value * 1000).toFixed(0)}ms`}`, {
      undoable: true,
      undo: () => setFadeOut(before),
      redo: () => setFadeOut(value),
    })
  }, [sessionLog])

  // Loudness preset is a discrete click; gain is a drag slider (§19.5),
  // so its log entry is debounced to the end of the drag rather than firing
  // once per pixel — the rail is a record of decisions, not of pointermoves.
  const handlePresetChange = useCallback((preset: LoudnessPreset) => {
    const before = loudnessPreset
    setLoudnessPreset(preset)
    setProcessedAudio(null)
    setMeasuredLoudnessDb(null)
    if (before === preset) return
    sessionLog.addEntry('gain', `Loudness preset: ${LOUDNESS_PRESETS[preset].label}`, {
      undoable: true,
      undo: () => setLoudnessPreset(before),
      redo: () => setLoudnessPreset(preset),
    })
  }, [loudnessPreset, sessionLog])
  const handleGainChange = useCallback((value: number) => {
    if (gainDragStartRef.current === null) gainDragStartRef.current = gainRef.current
    setFineTuneGainDb(value)
    setProcessedAudio(null)
    setMeasuredLoudnessDb(null)
    if (gainLogTimer.current) clearTimeout(gainLogTimer.current)
    gainLogTimer.current = setTimeout(() => {
      const before = gainDragStartRef.current
      gainDragStartRef.current = null
      if (before === null || before === value) return
      sessionLog.addEntry('gain', `Gain ${value > 0 ? '+' : ''}${value.toFixed(1)} dB`, {
        undoable: true,
        undo: () => setFineTuneGainDb(before),
        redo: () => setFineTuneGainDb(value),
      })
    }, 400)
  }, [sessionLog])

  // Handle playback change
  const handlePlaybackChange = useCallback((playing: boolean) => {
    setIsPlaying(playing)
  }, [])

  // Handle play/pause
  const handlePlayPause = useCallback(() => {
    // Toggle play state - useTrimPlayback handles actual playback
    if (!isPlaying) {
      trackPreviewPlay({
        sound_id: 'custom-upload',
        sound_title: selectedFile?.name || 'Custom Upload',
        source: 'create_page',
      })
    }
    setIsPlaying(prev => !prev)
  }, [isPlaying, selectedFile?.name])

  // Process and download audio in one action
  const handleDownload = useCallback(async () => {
    if (!audioBuffer) return
    setFileError(null)
    setDownloadedBlob(null)

    trackDownloadButtonClick({
      sound_id: 'custom-upload',
      sound_title: selectedFile?.name || 'Custom Upload',
      source: 'create_page',
    })
    trackDownloadInitiated({
      sound_id: 'custom-upload',
      sound_title: selectedFile?.name || 'Custom Upload',
      source: 'create_page',
    })

    // If already processed with same settings, just download
    if (processedBlob) {
      const result = await downloadFile({
        blob: processedBlob,
        filename: TESLA_REQUIREMENTS.FILENAME,
        accountingTarget: downloadAccountTarget(libraryExportSoundId({
          soundId: deepLinkSoundId,
          librarySoundLoaded: Boolean(deepLinkLabel),
        })),
        onError: error => setFileError(downloadFailureMessage(error)),
      })
      if (!result.success) return
      setDownloadedBlob(processedBlob)
      sessionLog.addEntry(
        'export',
        `Exported ${TESLA_REQUIREMENTS.FILENAME} · ${formatFileSizeForLog(processedBlob.size)}`,
        { undoable: false }
      )

      trackDownloadCompleted({
        sound_id: 'custom-upload',
        sound_title: selectedFile?.name || 'Custom Upload',
        source: 'create_page',
        trim_used: regionStart > 0 || regionEnd < (audioBuffer?.duration || TESLA_REQUIREMENTS.MAX_DURATION),
      })

      // Arm the return-visit install check-in (components/ui/install-checkin.tsx)
      // and the /guide attribution behind `guide_visited_post_download`
      // (TES-1577 — this path recorded neither). Deep-linked conversions use the
      // real sound id; free uploads share a stable synthetic id — both helpers
      // tolerate any string key.
      const installKey = deepLinkSoundId ?? 'custom-upload'
      const installLabel = deepLinkLabel || selectedFile?.name || 'Custom Upload'
      recordPendingInstall(installKey, installLabel)
      markDownloadCompleted({
        sound_id: installKey,
        sound_title: installLabel,
        source: 'create_page',
      })
      return
    }

    // Process first, then download
    setIsProcessing(true)
    try {
      const targetRmsDb = LOUDNESS_PRESETS[loudnessPreset].targetRmsDb
      const blob = await optimizeForTeslaWithFades(audioBuffer, {
        startTime: regionStart,
        endTime: regionEnd,
        targetRmsDb,
        fineTuneGainDb,
        fadeIn,
        fadeOut,
      })

      // Decode for preview, measure loudness, and store alongside the settings used
      const arrayBuffer = await blob.arrayBuffer()
      const audioContext = new AudioContext()
      // audio-egress-allow: trimmer on /create: the ?sound=<id> deep link or the file the visitor exports
      const processedBuffer = await audioContext.decodeAudioData(arrayBuffer)
      await audioContext.close()
      const loudness = measureLoudnessDb(processedBuffer)
      setMeasuredLoudnessDb(isFinite(loudness) ? loudness : null)
      setProcessedAudio({
        blob,
        audioBuffer: processedBuffer,
        loudnessPreset,
        fineTuneGainDb,
        fadeIn,
        fadeOut,
        regionStart,
        regionEnd,
        isNearSilent: !isFinite(loudness) || loudness < SILENCE_THRESHOLD_DB,
      })
      setPreviewMode('processed')
      trackCreateStep({ tool: 'convert', step: 'converted' })

      // Trigger download
      const result = await downloadFile({
        blob,
        filename: TESLA_REQUIREMENTS.FILENAME,
        accountingTarget: downloadAccountTarget(libraryExportSoundId({
          soundId: deepLinkSoundId,
          librarySoundLoaded: Boolean(deepLinkLabel),
        })),
        onError: error => setFileError(downloadFailureMessage(error)),
      })
      if (!result.success) return
      setDownloadedBlob(blob)
      sessionLog.addEntry(
        'export',
        `Exported ${TESLA_REQUIREMENTS.FILENAME} · ${formatFileSizeForLog(blob.size)}`,
        { undoable: false }
      )

      // Track download completion for GA4
      trackDownloadCompleted({
        sound_id: 'custom-upload',
        sound_title: selectedFile?.name || 'Custom Upload',
        source: 'create_page',
        trim_used: regionStart > 0 || regionEnd < (audioBuffer?.duration || TESLA_REQUIREMENTS.MAX_DURATION),
      })

      // Arm the return-visit install check-in (components/ui/install-checkin.tsx)
      // and the /guide attribution behind `guide_visited_post_download`
      // (TES-1577 — this path recorded neither). Deep-linked conversions use the
      // real sound id; free uploads share a stable synthetic id — both helpers
      // tolerate any string key.
      const installKey = deepLinkSoundId ?? 'custom-upload'
      const installLabel = deepLinkLabel || selectedFile?.name || 'Custom Upload'
      recordPendingInstall(installKey, installLabel)
      markDownloadCompleted({
        sound_id: installKey,
        sound_title: installLabel,
        source: 'create_page',
      })
    } catch (error) {
      Sentry.captureException(error)
      setFileError('Export failed: the audio could not be processed. Press Download to try again.')
      trackCreateStep({ tool: 'convert', step: 'failed', reason: 'export' })
      sessionLog.addEntry('export', 'Export failed. Could not process the audio.', { undoable: false, announce: 'assertive' })
    } finally {
      setIsProcessing(false)
    }
  }, [audioBuffer, processedBlob, regionStart, regionEnd, loudnessPreset, fineTuneGainDb, fadeIn, fadeOut, selectedFile, downloadFile, deepLinkSoundId, deepLinkLabel, sessionLog])

  const trimDuration = regionEnd - regionStart
  const canProcess = audioBuffer !== null && trimDuration > 0 && trimDuration <= TESLA_REQUIREMENTS.MAX_DURATION
  const hasProcessedAudio = processedBlob !== null
  const downloadCompleted = downloadedBlob !== null && downloadedBlob === processedBlob

  // One-line summary shown when the fine-tune section is collapsed
  const fadesSummary =
    fadeIn === 0 && fadeOut === 0
      ? 'no fades'
      : `fade in ${formatFadeMs(fadeIn)}, fade out ${formatFadeMs(fadeOut)}`
  const fineTuneSummary = `${LOUDNESS_PRESETS[loudnessPreset].label} loudness${
    fineTuneGainDb !== 0 ? ' (adjusted)' : ''
  }, ${fadesSummary}`

  return (
    <div className="flex flex-col gap-8 lg:flex-row lg:items-start lg:gap-6">
      <FileMismatchModal
        open={mismatch !== null}
        previousFileName={pendingReturnState?.fileName ?? ''}
        newFileName={mismatch?.file.name ?? ''}
        onReapply={handleMismatchReapply}
        onDiscard={handleMismatchDiscard}
      />

      <div className="min-w-0 flex-1 space-y-8">
      <aside className="py-1">
        <p className="text-sm text-[var(--tls-fg-muted)]">
          Your file stays on your device. Trimming and export happen in your browser, and nothing
          is uploaded.
        </p>
      </aside>

      {pendingReturnState && !audioBuffer && !mismatch && (
        <section className="border-t border-[color:var(--tls-border-subtle)] pt-6">
          <ReturnStateBanner state={pendingReturnState} />
        </section>
      )}

      <section>
        <SectionHead size="block">{deepLinkLabel ? `Editing: ${deepLinkLabel}` : 'Upload audio'}</SectionHead>
        <FileDropzone
          onFileSelect={handleFileSelect}
          isLoading={isLoadingFile}
          error={fileError}
          selectedFile={selectedFile}
          onClear={handleFileClear}
        />
        {/* §19.3 WARN state: usable, not a failure — no border-2/ink treatment,
            just a role=status line naming the tradeoff. Dismissable rather
            than pinned, since it is advisory rather than actionable. */}
        {lowSampleRateWarning && (
          <div role="status" className="mt-3 flex items-start justify-between gap-3 border-t border-[color:var(--tls-border-subtle)] pt-3">
            <p className="text-sm text-[var(--tls-fg-muted)]">{lowSampleRateWarning}</p>
            <button
              type="button"
              onClick={() => setLowSampleRateWarning(null)}
              className="shrink-0 text-xs font-medium text-[var(--tls-brand-ink)] underline-offset-2 hover:underline"
            >
              Dismiss
            </button>
          </div>
        )}
      </section>

      {audioBuffer && (
        <section className="border-t border-[color:var(--tls-border-subtle)] pt-6">
          <SectionHead size="block" ref={trimHeadingRef} tabIndex={-1}>Trim</SectionHead>

          <div className="space-y-6">
            <WaveformTrimmer
              audioBuffer={previewMode === 'processed' && processedAudioBuffer ? processedAudioBuffer : audioBuffer}
              onRegionChange={handleRegionChange}
              region={{ start: regionStart, end: regionEnd }}
              sourceDuration={metadata?.duration}
              locked={previewMode === 'processed' && processedAudioBuffer !== null}
              onPlaybackChange={handlePlaybackChange}
              isPlaying={isPlaying}
              isLoopEnabled={isLoopEnabled}
            />

            <AudioControls
              isPlaying={isPlaying}
              isLoopEnabled={isLoopEnabled}
              previewMode={previewMode}
              hasProcessedAudio={hasProcessedAudio}
              onPlayPause={handlePlayPause}
              onLoopToggle={() => setIsLoopEnabled(prev => !prev)}
              onPreviewModeChange={setPreviewMode}
            />

            {/* Progressive disclosure: loudness + fades are secondary decisions.
                Closed by default with sensible presets already applied; the
                summary line below always reflects current settings. */}
            <div className="border-t border-[color:var(--tls-border-subtle)] pt-4">
              <button
                type="button"
                onClick={() => setIsFineTuneOpen(prev => !prev)}
                aria-expanded={isFineTuneOpen}
                aria-controls="fine-tune-panel"
                className={cn(
                  // min-h ensures 44px WCAG 2.5.5 touch target
                  'flex min-h-[44px] w-full items-center justify-between gap-3 rounded-[var(--ls-radius-0)] px-1 text-left transition-colors duration-[var(--motion-duration-standard)] ease-[var(--motion-ease-standard)]',
                  'tlsr-focus'
                )}
              >
                <span className="min-w-0">
                  <span className="block text-sm font-medium text-[var(--tls-fg-strong)]">
                    Fine-tune loudness &amp; fades (optional)
                  </span>
                  <span className="block truncate text-xs text-[var(--tls-fg-subtle)]">
                    {fineTuneSummary}
                  </span>
                </span>
                <ChevronDown
                  aria-hidden="true"
                  className={cn(
                    'size-4 shrink-0 text-[var(--tls-fg-subtle)] transition-transform duration-[var(--motion-duration-standard)] ease-[var(--motion-ease-standard)]',
                    isFineTuneOpen && 'rotate-180'
                  )}
                />
              </button>

              {isFineTuneOpen && (
                <div id="fine-tune-panel" className="mt-4 grid gap-6 md:grid-cols-2">
                  <LoudnessControls
                    preset={loudnessPreset}
                    fineTuneGainDb={fineTuneGainDb}
                    measuredLoudnessDb={measuredLoudnessDb}
                    onPresetChange={handlePresetChange}
                    onFineTuneGainChange={handleGainChange}
                  />
                  <FadeControls
                    fadeIn={fadeIn}
                    fadeOut={fadeOut}
                    maxDuration={trimDuration}
                    onFadeInChange={handleFadeInChange}
                    onFadeOutChange={handleFadeOutChange}
                  />
                </div>
              )}
            </div>
          </div>
        </section>
      )}

      {audioBuffer && (
        <section className="border-t border-[color:var(--tls-border-subtle)] pt-6">
          <SectionHead size="block">Export</SectionHead>

          <div className="space-y-6">
            <CompatibilityIndicator
              metadata={metadata}
              sourceSizeBytes={selectedFile?.size}
              trimDuration={trimDuration}
              isProcessing={isProcessing}
            />

            <UndoHistory sessionLog={sessionLog} />

            {isNearSilent && (
              <div
                role="status"
                className="flex items-start gap-3 border-t border-[color:var(--tls-border-subtle)] pt-3 text-sm text-[var(--tls-fg-strong)]"
              >
                <AlertTriangle className="mt-0.5 size-4 shrink-0" aria-hidden="true" />
                <span className="font-medium">This clip is nearly silent. Pick a louder section or a different file.</span>
              </div>
            )}

            <DownloadPanel
              canProcess={canProcess}
              isProcessing={isProcessing}
              downloadCompleted={downloadCompleted}
              trimDuration={trimDuration}
              onDownload={handleDownload}
              soundId={deepLinkSoundId}
              soundTitle={deepLinkLabel || undefined}
            />
          </div>
        </section>
      )}

      <section className="border-y border-[color:var(--tls-border-subtle)] py-5 md:py-6">
        <SectionHead size="block">The file you get</SectionHead>
        <div className="space-y-3 text-sm text-[var(--tls-fg-muted)]">
          <p>Tesla only requires a WAV named LockChime.wav. Every export also follows the recommended settings:</p>
          <dl className="grid border-t border-[color:var(--tls-border-subtle)] sm:grid-cols-2 sm:gap-x-8">
            {teslaRequirementItems.map((item) => (
              <div key={item.label} className="border-b border-[color:var(--tls-border-subtle)] py-3">
                <dt className="font-medium text-[var(--tls-fg-strong)]">{item.label}</dt>
                <dd className="mt-1">{item.copy}</dd>
              </div>
            ))}
          </dl>
        </div>
      </section>

      <ActivityStatusRow log={sessionLog.log} />
      </div>

      <ActivityRailDesktop log={sessionLog.log} className="lg:sticky lg:top-6" />
    </div>
  )
}
